import { ID, IDRecord } from '../../../types/UtilityTypes'
import { ServerTask, Task, TaskRecord } from '../../../responses/tasks'
import { createReducer } from 'typesafe-actions'
import { normalizeTask, normalizeTasks } from '../../../utils/normalize'
import { combineReducers } from 'redux'
import { eqId, getId, idRemove, idReplace } from '../../../utils/entity-utils'
import * as A from 'fp-ts/es6/Array'
import { array } from 'fp-ts/es6/Array'
import * as R from 'fp-ts/es6/Record'
import { flow, identity } from 'fp-ts/es6/function'
import { defaultValueW, modifyProp, setProp, snoc } from '../../../utils/utils'
import { departmentsTasks } from '../../../utils/lenses'
import { taskListActions } from '../../pages/task-list/task-list-actions'
import { taskActions } from './task-actions'
import { RequestStatus } from '../../../responses/response'
import { pipe } from 'fp-ts/es6/pipeable'
import { head, tail } from 'fp-ts/es6/NonEmptyArray'
import { projectActions } from '../project/project-actions'
import { statusReducer } from '../../../utils/redux-utils'
import { objectActions } from '../object/object-actions'
import { getLastSemigroup } from 'fp-ts/es6/Semigroup'
import { historyActions } from '../../global/history/history-actions'
import { UserWithTasks } from '../../../responses/users'
import { DepartmentWithUsers } from '../../../responses/departments'
import { ADTType, makeADT, ofType } from '@morphic-ts/adt'


const departmentsToTasks = flow(
    A.map((department: DepartmentWithUsers) => department.users),
    A.flatten,
    A.reduce<UserWithTasks, IDRecord>({}, (state, user) => ({
        ...state,
        [user.id]: user.tasks.map(getId),
    }))
)

const mapIdArray = R.fromFoldableMap(getLastSemigroup<ID[]>(), array)


export const TaskEditState = makeADT('type')({
    none: ofType<{type: 'none'}>(),
    update: ofType<{type: 'update', task: Task}>(),
    delete: ofType<{type: 'delete', task: Task, message: string}>(),
    create: ofType<{type: 'create', objectId: ID}>(),
    append: ofType<{type: 'append', objectId: ID, parentId: ID}>()
})

export interface TaskState {
    updateStatus: RequestStatus,
    editState: ADTType<typeof TaskEditState>,
    byId: TaskRecord,
    byParent: IDRecord,
    byObject: IDRecord,
    byUser: IDRecord,
    results: IDRecord,
}

export const taskReducer = combineReducers<TaskState>({
    updateStatus: statusReducer(taskActions.update),
    editState: createReducer<ADTType<typeof TaskEditState>>(TaskEditState.as.none({}))
        .handleAction(taskActions.cancelEdit, () => TaskEditState.as.none({}))
        .handleAction(taskActions.startUpdate, (_, { payload }) => TaskEditState.as.update({task: payload}))
        .handleAction(taskActions.startDelete, (_, { payload }) => TaskEditState.as.delete(payload))
        .handleAction(taskActions.startCreate, (_, { payload }) => TaskEditState.as.create(payload))
        .handleAction(taskActions.startAppend, (_, { payload }) => TaskEditState.as.append(payload))
        .handleAction(taskActions.update.success, (state, action) => {
            return action.meta === 'user' ? TaskEditState.as.none({}) : state
        })
        .handleAction(taskActions.create.success, (state, action) => {
            if (TaskEditState.isAnyOf(['create', 'update', 'append'])(state) && action.meta === 'user') {
                return TaskEditState.as.none({})
            } else {
                return state
            }
        }),
    byId: createReducer<TaskRecord>({})
        .handleAction(taskActions.parents.success, (state, action) => ({
            ...state,
            ...R.fromFoldableMap(getLastSemigroup<Task>(), array)(
                action.payload.tasks.flatMap(parent => [parent, ...parent.children ?? []]),
                task => [task.id.toString(), task],
            )
        }))
        .handleAction(projectActions.get.success, (state, action) => ({
            ...state,
            ...R.fromFoldableMap(getLastSemigroup<Task>(), array)(
                action.payload.project_work_objects.flatMap(object => object.tasks).flatMap(parent => [parent, ...parent.children ?? []]),
                task => [task.id.toString(), task],
            )
        }))
        .handleAction([
            taskActions.user.success
        ], (state, action) => normalizeTasks(action.payload.tasks)(state))
        .handleAction(taskActions.requested.success, (state, action) =>
            normalizeTasks(action.payload)(state))
        .handleAction([
            taskActions.get.success,
            taskActions.update.success
        ], (state, action) => normalizeTasks([action.payload, ...action.payload.children || []])(state))
        .handleAction(taskActions.create.success, (state, action) =>
            modifyProp<TaskRecord, ID>(action.payload.id, normalizeTask(action.payload))(state))
        .handleAction(taskListActions.departments.success, (state, action) =>
            normalizeTasks(departmentsTasks(action.payload))(state))
        .handleAction(projectActions.close.success, (state, action) =>
            normalizeTasks(action.payload.project_work_objects.flatMap(object => object.tasks))(state))
        .handleAction(objectActions.close.success, (state, action) => normalizeTasks(action.payload.tasks)(state)),
        byParent: createReducer<IDRecord>({})
        .handleAction(taskActions.get.success, (state, action) =>
            pipe(state, setProp(action.payload.id, action.payload.children.map(getId) ?? []))
        )
        .handleAction(taskActions.parents.success, (state, action) => ({
            ...state,
            ...mapIdArray(action.payload.tasks, task => [task.id.toString(), task.children?.map(getId) ?? []])
        }))
        .handleAction(projectActions.get.success, (state, action) => ({
            ...state,
            ...R.fromFoldableMap(getLastSemigroup<ID[]>(), array)(
                action.payload.project_work_objects.flatMap(object => object.tasks),
                (parent: ServerTask) => [parent.id.toString(), parent.children?.map(getId) ?? []]
            )
        }))
        .handleAction(taskActions._delete, (state, { payload }) => pipe(state,
            R.map(idRemove(payload.task)),
            A.isNonEmpty(payload.children) ?
                setProp<IDRecord, ID>(head(payload.children), tail(payload.children)) :
                identity,
        ))
        .handleAction(taskActions.create.success, (state, action) => pipe(state,
            action.payload.parent_id ?
                modifyProp<IDRecord, ID>(action.payload.parent_id, flow(
                    snoc(action.payload.id),
                    A.uniq(eqId),
                )) :
                identity
        )),
    byObject: createReducer<IDRecord>({})
        .handleAction(taskActions.parents.success, (state, action) => pipe(
            state,
            setProp(action.payload.objectId, action.payload.tasks.map(getId))
        ))
        .handleAction(taskActions.get.success, (state, action) =>
            pipe(state, modifyProp(action.payload.project_work_object_id, flow(defaultValueW([]), snoc(action.payload.id))))
        )
        .handleAction(projectActions.get.success, (state, action) => ({
            ...state,
            ...mapIdArray(
                action.payload.project_work_objects,
                object => [object.id.toString(), object.tasks.map(getId)]
            )
        }))
        .handleAction(taskActions._delete, (state, { payload }) =>
            R.map(A.isNonEmpty(payload.children) ?
                idReplace(payload.task, head(payload.children)) :
                idRemove(payload.task)
            )(state)
        )
        .handleAction(taskActions.create.success, (state, action) => {
            if (action.payload.parent_id) {
                return state
            }
            return pipe(state, modifyProp(action.payload.project_work_object_id, flow(
                snoc(action.payload.id),
                A.uniq(eqId),
            )))
        }),
    byUser: createReducer<IDRecord>({})
        .handleAction(taskListActions.departments.success, (state, action) => ({
            ...state,
            ...departmentsToTasks(action.payload),
        }))
        .handleAction(taskActions.create.success, (state, action) => {
            if (action.payload.target_user?.id) {
                return pipe(state, modifyProp(action.payload.target_user.id, flow(
                    defaultValueW([]),
                    snoc(action.payload.id),
                    A.uniq(eqId),
                )))
            } else {
                return state
            }
        })
        .handleAction(taskActions._delete, (state, { payload }) => R.map(idRemove(payload.task))(state))
        .handleAction(taskActions.user.success, (state, action) => ({
            ...state,
            [action.payload.userId]: action.payload.tasks.map(getId),
        })),
    results: createReducer<IDRecord>({})
        .handleAction(projectActions.get.success, (state, action) => ({
            ...state,
            ...mapIdArray(
                action.payload.project_work_objects.flatMap(object => object.tasks).flatMap(parent => [parent, ...parent.children ?? []]),
                (task: ServerTask) => [task.id.toString(), task.results.map(getId)]
            )
        }))
        .handleAction(taskActions.parents.success, (state, action) => ({
            ...state,
            ...mapIdArray(
                action.payload.tasks.flatMap(parent => [parent, ...parent.children ?? []]),
                task => [task.id.toString(), task.results.map(getId)]
            )
        }))
        .handleAction([taskActions.get.success, taskActions.update.success], (state, action) => ({
            ...state,
            ...mapIdArray(
                [action.payload, ...action.payload.children ?? []],
                task => [task.id.toString(), task.results.map(getId)]
            )
        }))
        .handleAction(historyActions.comment.success, (state, action) => {
            if (action.payload.owner_type === 'task' && action.payload.resulted) {
                return pipe(state, modifyProp(action.payload.owner_id, flow(
                    snoc(action.payload.id),
                    A.uniq(eqId),
                )))
            } else {
                return state
            }
        })
        .handleAction(taskActions.comments.success, (state, action) => {
            if (action.payload.resulted) {
                return pipe(state, setProp(action.payload.taskId, action.payload.comments.map(getId)))
            } else {
                return state
            }
        })
})

